function makeIdPath(base: string) {
  return (id: string): string => {
    if (id) {
      return `${base}/${id}`
    }
    return `${base}/:id`
  }
}

/**
 * 특정 path에 둘 이상의 URL 파라미터를 추가할 때 사용하는 함수
 * 타입 파라미터 `P`에 `[type: string, id: string]`처럼 튜플 타입을 지정하면
 * `siteUrl`을 사용하는 컴포넌트에서 전달해야할 파라미터의 개수와 타입을 알 수 있으므로 설정하는 것을 추천.
 * @param base
 */
function makeMultipleParams<P extends string[]>(base: string) {
  return (...params: P) => {
    if (params.length > 0) {
      return `${base}/${params.join('/')}`
    }
    return `${base}/:id`
  }
}

function makeProjectIdPath(base: string, path?: string) {
  return (id: string, nodeId?: string): string => {
    if (path && nodeId) {
      return `${base}/${id}/${path}/${nodeId}`
    }
    if (id && path) {
      return `${base}/${id}/${path}`
    }
    if (id) {
      return `${base}/${id}`
    }
    return `${base}/:id`
  }
}

const dashboardRoot = '/main/dashboard'
const databaseRoot = '/main/database'

const loginRoot = '/login'
const workspaceRoot = '/main/workspace'
const projectRoot = '/main/project'
const labelingRoot = 'labeling'
const resultRoot = 'result'

export const SiteUrls = {
  Landing: '/',
  Main: {
    _: '/main',
    Dashboard: {
      _: dashboardRoot,
      Edit: `${dashboardRoot}/edit`,
      WidgetConfig: `${dashboardRoot}/widgetConfig`,
    },
    // Database: {
    //   _: databaseRoot,
    //   List: (page: number) => `${databaseRoot}?page=${page}`,
    //   Collections: (id: string, page?: number) => (page ? `${databaseRoot}/${id}?page=${page}` : `${databaseRoot}/${id}`),
    //   CollectionsCreate: makeIdPath(`${databaseRoot}/create`),
    //   CollectionsDetail: makeMultipleParams<[databaseId: string, collectionId: string]>(databaseRoot),
    // },
    // Pipeline: '/main/pipeline',
    Workspace: {
      Main: workspaceRoot,
      List: `${workspaceRoot}/list`,
      Join: `${workspaceRoot}/join`,
      Create: `${workspaceRoot}/create`,
      Invited: makeMultipleParams<[invitationCode: string]>(workspaceRoot),
      Settings: `${workspaceRoot}/settings`,
      NotificationSettings: `${workspaceRoot}/notification/settings`,
      Projects: `${workspaceRoot}/projects`,
      WorkspaceTaskList: `${workspaceRoot}/taskList`,
      Members: `${workspaceRoot}/members`,
      TeamsMembers: `${workspaceRoot}/teams/members`,
      Pricing: `${workspaceRoot}/pricing`,
      Storage: {
        _: `${workspaceRoot}/storage`,
        folder: makeIdPath(`${workspaceRoot}/storage`),
      },
      AutoCollectedStorage: {
        _: `${workspaceRoot}/autoCollectedStorage`,
        folder: makeIdPath(`${workspaceRoot}/autoCollectedStorage`),
      },
      UnusedStorageItems: {
        _: `${workspaceRoot}/unusedStorageItems`,
      },
      ResourceMonitoring: `${workspaceRoot}/resourceMonitoring`,
      DiskSpaceManagement: `${workspaceRoot}/diskSpaceManagement`,
      ImportModel: `${workspaceRoot}/importModel`,
      DataCollection: {
        _: `${workspaceRoot}/dataCollection`,
        Endpoint: `${workspaceRoot}/dataCollection/endpoint`,
        Collections: `${workspaceRoot}/dataCollection/collections`,
        CollectionDataList: makeIdPath(`${workspaceRoot}/dataCollection/collection`),
        CollectionSchemas: `${workspaceRoot}/dataCollection/schemas`,
      },
      AutoDeploy: {
        _: `${workspaceRoot}/autoDeploy`,
        History: makeIdPath(`${workspaceRoot}/autoDeploy/history`),
      },
      Credit: {
        _: `${workspaceRoot}/credit`,
        Stats: `${workspaceRoot}/credit/stats`,
      },
      NoWorkspace: `${workspaceRoot}/none`,
    },
    // FIXME: 구조 잡은 후에 기존에 있던 Project 경로 제거 후 이름 변경
    Project: {
      Main: makeIdPath(projectRoot),
      ModelDownloadHistory: makeProjectIdPath(projectRoot, 'modelDownload/history'),
      ModelRetrainHistory: makeProjectIdPath(projectRoot, 'modelRetrain/history'),
      /** @deprecated */
      OldPipeline: makeProjectIdPath(projectRoot, 'pipeline_old'),
      ModelManagement: makeProjectIdPath(projectRoot, 'model/management'),
      Dashboard: makeProjectIdPath(projectRoot, 'dashboard'),
      Pipeline: makeProjectIdPath(projectRoot, 'pipeline'),
      OpsPipeline: makeProjectIdPath(projectRoot, 'pipeline/ops'),
      Labeling: {
        Detail: makeProjectIdPath(projectRoot, `${labelingRoot}/detail`),
        Tabular: makeProjectIdPath(projectRoot, `${labelingRoot}/tabular`),
      },
      Result: {
        Model: makeProjectIdPath(projectRoot, `${resultRoot}/model`),
        ModelIe: makeProjectIdPath(projectRoot, `${resultRoot}/model/ie`),
        Validation: makeProjectIdPath(projectRoot, `${resultRoot}/validation`),
        ValidationIe: makeProjectIdPath(projectRoot, `${resultRoot}/validation/ie`),
        Inference: makeProjectIdPath(projectRoot, `${resultRoot}/inference`),
        InferenceIe: makeProjectIdPath(projectRoot, `${resultRoot}/inference/ie`),
        DataDrift: makeProjectIdPath(projectRoot, `${resultRoot}/datadrift`),
        ModelDrift: makeProjectIdPath(projectRoot, `${resultRoot}/modelDrift`),
        ModelPerformance: makeProjectIdPath(projectRoot, `${resultRoot}/modelPerformance`),
        ModelRetrainEvaluation: makeProjectIdPath(projectRoot, `${resultRoot}/modelRetrainEvaluation`),
        ModelPipelineInstance: makeProjectIdPath(projectRoot, `${resultRoot}/modelPipelineInstance`),
        ModelPipelineResult: makeProjectIdPath(projectRoot, `${resultRoot}/modelPipelineResult`),
        // 테이블 데이터 관련
        EDA: makeProjectIdPath(projectRoot, `${resultRoot}/eda`),
        ModelTabular: makeProjectIdPath(projectRoot, `${resultRoot}/model/tabular`),
        ValidationTabular: makeProjectIdPath(projectRoot, `${resultRoot}/validation/tabular`),
        InferenceTabular: makeProjectIdPath(projectRoot, `${resultRoot}/inference/tabular`),

      },
    },
    // Labeling: {
    //   Detail: makeIdPath(`${labelingRoot}/detail`),
    //   Classification: makeIdPath(`${labelingRoot}/classification`),
    // },
    // Result: {
    //   Model: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/model`),
    //   ModelIe: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/model/ie`),
    //   Validation: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/validation`),
    //   ValidationIe: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/validation/ie`),
    //   Inference: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/inference`),
    //   InferenceIe: makeMultipleParams<[projectId: string, nodeId: string]>(`${resultRoot}/inference/ie`),
    // },
    User: {
      Settings: '/main/user/settings',
      Inquiries: '/main/user/inquiries',
      InquiryDetail: makeIdPath('/main/user/inquiries'),
    },
    Manual: {
      List: '/main/manual/list',
    },
  },
  Login: {
    _: loginRoot,
    Main: `${loginRoot}/main`,
    SignUp: `${loginRoot}/signup`,
    RecoverPassword: `${loginRoot}/recover`,
    ExpiredInvitation: `${loginRoot}/expired`,
    ChangePassword: `${loginRoot}/changePassword`,
  },
}
